import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ItemSummaryCard from './ItemSummary';
import { API_BASE_URL } from '../urldata.js';
import SearchBox from '../components/input/SearchBox';
const NewItemsBar = ({ highlighted = false }) => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const scrollRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState('');
    const handleClear = () => {
        setSearchQuery('');
    };
    const navigate = useNavigate();
    const handleSearch = () => {
        navigate(`/itemlist?q=${encodeURIComponent(searchQuery)}`);
    };

    useEffect(() => {
        const fetchNewItems = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(`${API_BASE_URL}/tapi/search?sort=created_at:desc&page=1`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                // Assuming data.results or data contains the items list based on standard search API
                // If data is the array directly, use it. If it's paginated, usually it's in data.data or data.results.
                // Looking at common patterns, let's assume it's data.data or similar if it's paginated.
                // However, I'll check if data is an array first.
                const itemsList = Array.isArray(data) ? data : (data.data || data.results || []);
                setItems(itemsList);
            } catch (err) {
                console.error("Failed to fetch new items:", err);
                setError(err.message || "Failed to load new items.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchNewItems();
    }, []);

    const scroll = (direction) => {
        if (scrollRef.current) {
            const container = scrollRef.current;
            const scrollAmount = container.clientWidth * 0.8;
            const target = direction === 'left' ? -scrollAmount : scrollAmount;
            const start = container.scrollLeft;
            const duration = 1200;
            let startTime = null;

            const easeInOutQuad = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            const animate = (timestamp) => {
                if (!startTime) startTime = timestamp;
                const progress = timestamp - startTime;
                const fraction = Math.min(progress / duration, 1);
                const ease = easeInOutQuad(fraction);

                container.scrollLeft = start + target * ease;

                if (progress < duration) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        }
    };

    if (isLoading) return null; // Or a subtle loader
    if (error || items.length === 0) return null;

    return (
        <div className={`category-section-wrapper new-items-section mt-4 ${highlighted ? 'highlighted-bar' : 'odd'}`}>
            <div className="container-fluid px-4">
                <div className="category-header row g-3 align-items-center mx-0">
                    <div className="col-12 col-md-3 order-1 order-md-3">
                        <SearchBox
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onSearch={handleSearch}
                            onClear={handleClear}
                        />
                    </div>
                    <div className="d-flex align-items-center col-6 col-md-3 order-2 order-md-1">
                        <h2 className="category-title-link mb-0" style={{ cursor: 'default' }}>
                            Newly Added
                        </h2>
                        <span className="badge bg-danger ms-2">LATEST</span>
                    </div>
                    <div className="d-none d-md-block col-md-4 order-md-2"> </div>
                    <div className="category-controls col-6 col-md-2 order-3 order-md-4 justify-content-end">
                        <a href="/itemlist?sort=created_at:desc" className="show-all-btn p-2">
                            View All
                        </a>
                        <button className="scroll-btn" onClick={() => scroll('left')} aria-label="Scroll left">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                            </svg>
                        </button>
                        <button className="scroll-btn" onClick={() => scroll('right')} aria-label="Scroll right">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="category-items-row" ref={scrollRef}>
                    {items.map(item => (
                        <div key={item.id} style={{
                            flex: '0 0 auto',
                            width: '240px',
                            marginRight: '1rem',
                            display: 'flex',
                            scrollSnapAlign: 'start'
                        }}>
                            <ItemSummaryCard item={item} showDescription={false} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default NewItemsBar;
